// =====================
// Stars & Chores (Personal accounts — family daily-habit loop)
// =====================
//
// Shared contract for the stars/chores backend. All of these surfaces are
// PERSONAL-account only; the API verifies accountType === PERSONAL server-side
// and returns 403 for ORGANIZATION accounts.

/** Reason codes stored on a star_ledger row. */
export enum StarLedgerReason {
  CHORE_COMPLETED = 'chore_completed',
  REWARD_REDEEMED = 'reward_redeemed',
  ADJUSTMENT = 'adjustment',
}

/** A reward a family can redeem stars for. */
export interface Reward {
  id: string
  organizationId: string
  name: string
  starCost: number
  icon: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

/** One append-only ledger entry (signed delta). */
export interface StarLedgerEntry {
  id: string
  organizationId: string
  memberId: string
  delta: number
  reason: StarLedgerReason | string
  taskId: string | null
  rewardId: string | null
  createdAt: string
}

/**
 * Per-member roll-up for the Chore Chart widget: lifetime balance, this-week
 * activity, and current streak.
 */
export interface MemberStarSummary {
  memberId: string
  userId: string | null
  name: string | null
  image: string | null
  /** SUM(delta) over all time — never negative (redeem is floored at balance). */
  balance: number
  earnedThisWeek: number
  spentThisWeek: number
  choresCompletedThisWeek: number
  /** Consecutive days (org timezone) with >= 1 starred completion. */
  streak: number
}

/** Response for GET /stars/summary. */
export interface FamilyStarsSummary {
  members: MemberStarSummary[]
  /** ISO date (start of the current week, Monday) used for the this-week window. */
  weekStart: string
  weekEnd: string
}

/** Streak milestones that trigger a family celebration nudge. */
export const STREAK_MILESTONES = [3, 5, 7, 10, 14, 21, 30] as const

// ---- DTOs ----

export interface CreateRewardDto {
  name: string
  starCost: number
  icon?: string | null
  isActive?: boolean
}

export interface UpdateRewardDto {
  name?: string
  starCost?: number
  icon?: string | null
  isActive?: boolean
}

/**
 * Redeem a reward for a member. `memberId` optional — defaults to the calling
 * user's own member. The server validates the member has enough stars.
 */
export interface RedeemRewardDto {
  rewardId: string
  memberId?: string
}

/** Manual star adjustment (parent grants/removes stars). Admin/owner only. */
export interface AdjustStarsDto {
  memberId: string
  delta: number
  reason?: string
}

export interface StarLedgerPage {
  entries: StarLedgerEntry[]
  total: number
  page: number
  pageSize: number
}
